import type { JSX } from "react";
import { useMemo, useState } from "react";

import { UserOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormField, FormSubtitle, FormTitle } from "@impero-com/ui";
import { Checkbox, Input } from "antd";
import * as Cookies from "es-cookie";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { StatefulComponent, WithStatus } from "../../../utils/ajax";
import { t } from "../../../utils/i18n";
import { isEmailValid } from "../../../utils/isEmailValid";
import FormCard from "../../formCard";
import FormCardContainer from "../../formCardContainer";

import * as theme from "./theme.css";

const REMEMBER_ME_COOKIE = "impero_remember_me";

interface EmailFormValues {
    email: string;
    rememberMe: boolean;
}

type EmailFormStatus = { type: "DEFAULT" } | { type: "LOGGING_IN" };

interface EmailFormProps {
    rightSide: JSX.Element;
    title: string;
    footer?: JSX.Element;
    onSubmitEmail: (
        email: string,
        component: StatefulComponent<WithStatus<EmailFormStatus>>
    ) => void;
}

function EmailForm({
    rightSide,
    title,
    footer,
    onSubmitEmail,
}: EmailFormProps) {
    const rememberMeEmail = Cookies.get(REMEMBER_ME_COOKIE);

    const schema = useMemo(
        () =>
            z.object({
                email: z
                    .string()
                    .refine(isEmailValid, t("Login_UserNameRequired")),
                rememberMe: z.boolean(),
            }),
        []
    );

    const { control, handleSubmit } = useForm<EmailFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: rememberMeEmail ?? "",
            rememberMe: !!rememberMeEmail,
        },
    });

    const [status, setStatus] = useState<EmailFormStatus>({ type: "DEFAULT" });

    const submit = handleSubmit(({ email, rememberMe }) => {
        if (rememberMe) {
            Cookies.set(REMEMBER_ME_COOKIE, email, {
                sameSite: "lax",
                secure: true,
                expires: 30,
            });
        } else {
            Cookies.remove(REMEMBER_ME_COOKIE);
        }

        // The parent drives the login-options request through the AjaxManager,
        // which expects a class-like component with `state.status` and
        // `setState`. A submit always starts in the DEFAULT status, so we hand
        // it a self-contained shim that mirrors the loading status into React
        // state to drive the button spinner.
        const component: StatefulComponent<WithStatus<EmailFormStatus>> = {
            state: { status: { type: "DEFAULT" } },
            setState: (partial: Partial<WithStatus<EmailFormStatus>>) => {
                if (partial.status) {
                    component.state = { status: partial.status };
                    setStatus(partial.status);
                }
            },
        };
        onSubmitEmail(email, component);
    });

    return (
        <FormCardContainer rightSide={rightSide}>
            <FormTitle title={title} />
            <FormSubtitle subtitle={t("Login_EmailFormSubtitle")} />
            <FormCard onSubmitForm={(e) => void submit(e)}>
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                        <FormField error={fieldState.error?.message}>
                            <Input
                                {...field}
                                prefix={
                                    <UserOutlined
                                        className={theme.iconOpacity}
                                    />
                                }
                                placeholder={t("Login_UserName")}
                                autoFocus
                            />
                        </FormField>
                    )}
                />
                <div className={theme.rememberMe}>
                    <Controller
                        control={control}
                        name="rememberMe"
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={(e) =>
                                    field.onChange(e.target.checked)
                                }
                            >
                                {t("Login_RememberMe")}
                            </Checkbox>
                        )}
                    />
                    <Button
                        id="email-next-button"
                        variant="primary"
                        type="submit"
                        loading={status.type === "LOGGING_IN"}
                        block
                    >
                        {t("Login_EmailFormButton")}
                    </Button>
                </div>
            </FormCard>
            {footer}
        </FormCardContainer>
    );
}

export { type EmailFormProps, type EmailFormStatus, EmailForm };
